/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   MaintenanceHistoryList.tsx
 *     Description     :   Timeline component for maintenance history
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-19
 *     Modified        :   2026-01-19 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.11.0 (Specification Testing Standards Edition)
 *     Feature         :   028-lakehouse-maintenance
 * =========================================================================================
 */

import React from 'react';
import { CheckCircle2, AlertCircle, Hourglass, Wrench } from 'lucide-react';
import { Card, Badge, Spinner } from '../ui';
import type { BadgeProps } from '../ui';
import { useLakehouseHistory } from '../../hooks/useMaintenanceData';
import type { MaintenanceHistory } from '../../../../shared/types/maintenance';
import { formatEastern } from '@/utils/dateFormatter';

interface MaintenanceHistoryListProps {
  lakehouseId: string;
  compact?: boolean;
  limit?: number;
}

const operationIcons = {
  OPTIMIZE: Wrench,
  VACUUM: Wrench,
  ZORDER: Wrench,
};

// Status → timeline-dot color token (the colored marker on the rail).
const statusDotColor = {
  completed: 'var(--color-status-success)',
  failed: 'var(--color-status-danger)',
  running: 'var(--color-accent)',
  pending: 'var(--color-status-warning)',
  cancelled: 'var(--color-text-secondary)',
} as const;

// Status → Badge variant.
const statusBadgeVariant: Record<string, BadgeProps['variant']> = {
  completed: 'success',
  failed: 'error',
  running: 'info',
  pending: 'warning',
  cancelled: 'default',
};

const statusIcon = {
  completed: CheckCircle2,
  failed: AlertCircle,
  running: Hourglass,
  pending: Hourglass,
  cancelled: AlertCircle,
} as const;

export const MaintenanceHistoryList: React.FC<MaintenanceHistoryListProps> = ({
  lakehouseId,
  compact = false,
  limit = 10,
}) => {
  const { data, isLoading } = useLakehouseHistory(lakehouseId, 1, limit);

  if (isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Spinner />
      </div>
    );
  }

  if (!data?.data || data.data.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-[var(--color-text-secondary)]">No maintenance history</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {data.data.map((record: MaintenanceHistory, index: number) => {
        const statusKey = record.status as keyof typeof statusDotColor;
        const dotColor = statusDotColor[statusKey] ?? 'var(--color-text-secondary)';
        const badgeVariant = statusBadgeVariant[record.status] ?? 'default';
        const StatusIcon = statusIcon[statusKey] ?? AlertCircle;
        const OperationIcon =
          operationIcons[record.operationType as keyof typeof operationIcons] ?? Wrench;
        const isLast = index >= data.data.length - 1;

        const timestamp = record.startedAt
          ? formatEastern(record.startedAt)
          : formatEastern(record.createdAt);

        return (
          <div key={record.id} className="flex gap-3">
            {/* Timeline rail: status dot + connector */}
            <div className="flex flex-col items-center">
              <span
                className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-white"
                style={{ backgroundColor: dotColor }}
              >
                <StatusIcon className="h-4 w-4" />
              </span>
              {!isLast && <span className="w-px flex-1 bg-[var(--color-border)]" />}
            </div>

            <div className={compact ? 'mb-2 flex-1' : 'mb-4 flex-1'}>
              <Card variant={compact ? 'outlined' : 'elevated'} padding="sm">
                <div className="mb-2 flex items-center gap-2">
                  <OperationIcon className="h-4 w-4 text-[var(--color-text-secondary)]" />
                  <span className="text-sm font-medium text-[var(--color-text-primary)]">
                    {record.operationType}
                  </span>
                  <Badge variant={badgeVariant} className="capitalize">
                    {record.status}
                  </Badge>
                </div>
                {record.status === 'completed' && (
                  <div>
                    {record.filesAfter && (
                      <p className="text-sm text-[var(--color-text-secondary)]">
                        Files: {record.filesAfter}
                      </p>
                    )}
                    {record.storageReclaimedMb && (
                      <p className="text-sm text-[var(--color-text-secondary)]">
                        Storage reclaimed: {record.storageReclaimedMb.toFixed(2)} MB
                      </p>
                    )}
                    {record.effectiveRetentionDays && (
                      <p className="text-sm text-[var(--color-text-secondary)]">
                        Retention: {record.effectiveRetentionDays} days
                      </p>
                    )}
                    {record.completedAt && (
                      <p className="text-xs text-[var(--color-text-secondary)]">
                        Duration:{' '}
                        {Math.round(
                          (new Date(record.completedAt).getTime() -
                            new Date(record.startedAt!).getTime()) /
                            1000 /
                            60
                        )}{' '}
                        minutes
                      </p>
                    )}
                  </div>
                )}
                {record.status === 'failed' && record.errorMessage && (
                  <p className="text-sm text-[var(--color-status-danger)]">{record.errorMessage}</p>
                )}
                <p className="text-xs text-[var(--color-text-secondary)]">{timestamp}</p>
              </Card>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MaintenanceHistoryList;
